import { useState } from 'react'

import { t, translateApiError } from '../../../../../../common/i18n.js'
import {
    EXPERIENCE_TYPE_CONFIGS,
    type ExperienceEntry,
    type ExperienceInput,
    type ExperienceTypeConfig,
    formatPeriodDuration,
    formatPeriodMonth,
    isValidPeriodMonth,
    periodDurationMonths,
} from '../../../../../../common/portal-experience.js'
import { Language } from '../../../../../../common/types/i18n.js'
import { Button } from '../../../../../components/button/button.js'
import { TextField } from '../../../../../components/forms/text-field/text-field.js'
import { BookOpen, Plus, X } from '../../../../../components/icon/solar-icons.js'
import { Section, SectionHeader } from '../../../../../components/section/section.js'
import type { RequestConfirmFn } from '../employee-profile-types.js'
import '../employee-profile-view.css'

interface ExperiencesSectionProps {
    entries: ExperienceEntry[]
    canEdit: boolean
    lang: Language
    onAdd?: (data: ExperienceInput) => Promise<boolean>
    onUpdate?: (entryId: number, data: Omit<ExperienceInput, 'type'>) => Promise<boolean>
    onDelete?: (entryId: number) => Promise<boolean>
    requestConfirm: RequestConfirmFn
}

interface EntryDraft {
    name: string
    detail_1: string
    detail_2: string
    extra_info: string
    period_start: string
    period_end: string
}

type EntryDraftTextKey = 'name' | 'detail_1' | 'detail_2'

const emptyDraft: EntryDraft = {
    name: '',
    detail_1: '',
    detail_2: '',
    extra_info: '',
    period_start: '',
    period_end: '',
}

const draftFromEntry = (entry: ExperienceEntry): EntryDraft => ({
    name: entry.name,
    detail_1: entry.detail_1 ?? '',
    detail_2: entry.detail_2 ?? '',
    extra_info: entry.extra_info ?? '',
    period_start: entry.period_start ?? '',
    period_end: entry.period_end ?? '',
})

const formatPeriod = (entry: ExperienceEntry, config: ExperienceTypeConfig, lang: Language) => {
    const start = formatPeriodMonth(entry.period_start)
    const end = formatPeriodMonth(entry.period_end)
    // Ongoing work entries count their duration up to the current month.
    const durationEnd =
        entry.period_end ?? (config.ongoingWhenNoEnd ? new Date().toISOString().slice(0, 7) : null)
    const duration = formatPeriodDuration(
        periodDurationMonths(entry.period_start, durationEnd),
        lang,
    )
    const withDuration = (text: string) => (duration ? `${text} (${duration})` : text)
    if (start && end) return withDuration(`${start} – ${end}`)
    if (start && config.ongoingWhenNoEnd)
        return withDuration(`${start} – ${t.portal.profile.entryPresent(lang)}`)
    if (start) return start
    // An end-only period must not read like a start date.
    if (end) return `– ${end}`
    return ''
}

/** One repeater sub-block (education / work / training) with its add/edit form. */
const ExperienceBlock = ({
    config,
    entries,
    withManage,
    lang,
    onAdd,
    onUpdate,
    onDelete,
    requestConfirm,
}: {
    config: ExperienceTypeConfig
    entries: ExperienceEntry[]
    /** Section-level edit mode: manage affordances show only while it's on. */
    withManage: boolean
    lang: Language
    onAdd?: ExperiencesSectionProps['onAdd']
    onUpdate?: ExperiencesSectionProps['onUpdate']
    onDelete?: ExperiencesSectionProps['onDelete']
    requestConfirm: RequestConfirmFn
}) => {
    const ph = t.portal.profile

    // null = closed; 'new' = add form; number = editing that entry
    const [formTarget, setFormTarget] = useState<'new' | number | null>(null)
    const [draft, setDraft] = useState<EntryDraft>(emptyDraft)
    const [submitting, setSubmitting] = useState(false)

    const closeForm = () => {
        setFormTarget(null)
        setDraft(emptyDraft)
    }

    const openAdd = () => {
        setDraft(emptyDraft)
        setFormTarget('new')
    }

    const openEdit = (entry: ExperienceEntry) => {
        setDraft(draftFromEntry(entry))
        setFormTarget(entry.id)
    }

    // Firefox and desktop Safari render <input type="month"> as a plain text
    // box, so an invalid period can be typed — validate here rather than let
    // the server 400 silently.
    const periodInvalid = (v: string) => v !== '' && !isValidPeriodMonth(v)
    const rangeInvalid =
        Boolean(draft.period_start) &&
        Boolean(draft.period_end) &&
        !periodInvalid(draft.period_start) &&
        !periodInvalid(draft.period_end) &&
        draft.period_end < draft.period_start
    const periodNote = (v: string) =>
        periodInvalid(v)
            ? { text: translateApiError('EXPERIENCE_PERIOD_INVALID', {}, lang), isError: true }
            : undefined

    const submit = () => {
        const data = {
            name: draft.name.trim(),
            detail_1: draft.detail_1.trim() || null,
            detail_2: draft.detail_2.trim() || null,
            extra_info: config.hasExtraInfo ? draft.extra_info.trim() || null : null,
            period_start: draft.period_start || null,
            period_end: draft.period_end || null,
        }
        const action =
            formTarget === 'new'
                ? onAdd?.({ type: config.type, ...data })
                : typeof formTarget === 'number'
                  ? onUpdate?.(formTarget, data)
                  : undefined
        if (!action) return
        setSubmitting(true)
        void action.then((ok) => {
            setSubmitting(false)
            if (ok) closeForm()
        })
    }

    const fields: {
        key: EntryDraftTextKey
        label: ExperienceTypeConfig['nameLabel']
        required?: boolean
    }[] = [
        { key: 'name', label: config.nameLabel, required: true },
        ...(config.detail1Label ? [{ key: 'detail_1' as const, label: config.detail1Label }] : []),
        ...(config.detail2Label ? [{ key: 'detail_2' as const, label: config.detail2Label }] : []),
    ]

    return (
        <div className="portal-profile__comp-history">
            <div className="portal-profile__comp-header">
                <h4 className="portal-profile__subsection-title">
                    {config.title[lang]} ({entries.length})
                </h4>
                {withManage && onAdd && formTarget === null && (
                    <Button
                        variant="tertiary"
                        size="small"
                        iconLeft={Plus}
                        text={ph.addEntry(lang)}
                        onClick={openAdd}
                    />
                )}
            </div>

            {formTarget !== null && (
                <div className="portal-profile__contact-form">
                    {fields.map((field) => (
                        <div key={field.key} className="portal-profile__edit-field">
                            <label className="portal-profile__field-label">
                                {field.label[lang]}
                                {field.required ? ' *' : ''}
                            </label>
                            <TextField
                                value={draft[field.key]}
                                onChange={(value) =>
                                    setDraft((prev) => ({ ...prev, [field.key]: value }))
                                }
                            />
                        </div>
                    ))}
                    <div className="portal-profile__edit-field">
                        <label className="portal-profile__field-label">
                            {ph.periodStartMonth(lang)}
                        </label>
                        <TextField
                            inputProps={{ type: 'month', placeholder: 'YYYY-MM' }}
                            note={periodNote(draft.period_start)}
                            value={draft.period_start}
                            onChange={(value) =>
                                setDraft((prev) => ({ ...prev, period_start: value }))
                            }
                        />
                    </div>
                    <div className="portal-profile__edit-field">
                        <label className="portal-profile__field-label">
                            {ph.periodEndMonth(lang)}
                        </label>
                        <TextField
                            inputProps={{ type: 'month', placeholder: 'YYYY-MM' }}
                            note={
                                periodNote(draft.period_end) ??
                                (rangeInvalid
                                    ? { text: ph.periodEndBeforeStart(lang), isError: true }
                                    : undefined)
                            }
                            value={draft.period_end}
                            onChange={(value) =>
                                setDraft((prev) => ({ ...prev, period_end: value }))
                            }
                        />
                    </div>
                    {config.hasExtraInfo && (
                        <div className="portal-profile__edit-field">
                            <label className="portal-profile__field-label">
                                {ph.entryExtraInfo(lang)}
                            </label>
                            <TextField
                                as="textarea"
                                textAreaProps={{ rows: 3 }}
                                value={draft.extra_info}
                                onChange={(value) =>
                                    setDraft((prev) => ({ ...prev, extra_info: value }))
                                }
                            />
                        </div>
                    )}
                    <div className="portal-profile__edit-actions">
                        <Button
                            text={ph.cancelEdit(lang)}
                            variant="secondary"
                            size="small"
                            isDisabled={submitting}
                            onClick={closeForm}
                        />
                        <Button
                            text={submitting ? '...' : ph.saveChanges(lang)}
                            size="small"
                            isDisabled={
                                submitting ||
                                !draft.name.trim() ||
                                periodInvalid(draft.period_start) ||
                                periodInvalid(draft.period_end) ||
                                rangeInvalid
                            }
                            onClick={submit}
                        />
                    </div>
                </div>
            )}

            {entries.length === 0 && formTarget === null ? (
                <div className="portal-profile__field-value">{ph.noEntries(lang)}</div>
            ) : (
                entries.map((entry) => (
                    <div key={entry.id} className="portal-profile__contact-card">
                        <div>
                            <div className="portal-profile__contact-name">{entry.name}</div>
                            <div className="portal-profile__contact-detail">
                                {[entry.detail_1, entry.detail_2, formatPeriod(entry, config, lang)]
                                    .filter(Boolean)
                                    .join(' · ')}
                            </div>
                            {config.hasExtraInfo && entry.extra_info && (
                                <div className="portal-profile__contact-detail">
                                    {entry.extra_info}
                                </div>
                            )}
                        </div>
                        {withManage && (
                            <div className="portal-profile__edit-actions">
                                {onUpdate && formTarget === null && (
                                    <Button
                                        variant="tertiary"
                                        size="small"
                                        text={ph.editEntry(lang)}
                                        onClick={() => openEdit(entry)}
                                    />
                                )}
                                {onDelete && (
                                    <button
                                        type="button"
                                        className="portal-profile__contact-delete"
                                        title={ph.deleteEntry(lang)}
                                        onClick={() => {
                                            requestConfirm(ph.confirmDeleteEntry(lang), () => {
                                                // Don't leave the edit form open
                                                // over a deleted entry.
                                                if (formTarget === entry.id) closeForm()
                                                void onDelete(entry.id)
                                            })
                                        }}
                                    >
                                        <X size={14} />
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                ))
            )}
        </div>
    )
}

/**
 * "Kogemused" (TASK-300): education / work / training repeater entries.
 * Visible to self, direct manager and Admin (gated by the parent on
 * viewer.canViewExperiences); add/edit/delete on canEditExperiences. One
 * "Muuda" in the section header opens the manage affordances, like the
 * other profile sections.
 */
export const ExperiencesSection = ({
    entries,
    canEdit,
    lang,
    onAdd,
    onUpdate,
    onDelete,
    requestConfirm,
}: ExperiencesSectionProps) => {
    const ph = t.portal.profile
    const [isEditing, setIsEditing] = useState(false)

    return (
        <Section>
            <SectionHeader
                icon={BookOpen}
                title={ph.experiencesSection(lang)}
                actions={
                    canEdit && (
                        <Button
                            variant="secondary"
                            size="small"
                            text={isEditing ? ph.doneEditing(lang) : ph.editProfile(lang)}
                            onClick={() => setIsEditing((prev) => !prev)}
                        />
                    )
                }
            />
            {EXPERIENCE_TYPE_CONFIGS.map((config) => (
                <ExperienceBlock
                    // Remounting on mode toggle also closes any open entry form.
                    key={`${config.type}:${isEditing ? 'edit' : 'view'}`}
                    config={config}
                    entries={entries.filter((e) => e.type === config.type)}
                    withManage={canEdit && isEditing}
                    lang={lang}
                    onAdd={onAdd}
                    onUpdate={onUpdate}
                    onDelete={onDelete}
                    requestConfirm={requestConfirm}
                />
            ))}
        </Section>
    )
}
